import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { customFirestore } from "../firebase";
import {
  type BuildData,
  type CachedScreenData,
  type CompanyData,
  type PromptStep,
  type RunExecutionData,
  type SessionConfigData,
  type Test,
  type TestSettings,
} from "../utils/types";
import {
  getGptDriverInput,
  groupSteps,
  prepareCachedFlow,
  mergeLaunchParams,
} from "../utils/helpers";

interface BaseConfig {
  platform: string;
  device: string;
  language: string;
  testId: string;
  cachingEnabled: boolean;
}

interface ProcessStepsResult {
  runs: any[];
  hasSubtests: boolean;
}

const processGroupedSteps = async (
  groupedSteps: PromptStep[][],
  config: BaseConfig & {
    test?: Test;
    isPartialExecution?: boolean;
    fromParentRun: boolean;
    parsedNestedDependencies?: RunExecutionData[];
  }
): Promise<ProcessStepsResult> => {
  const runs: any[] = [];
  let hasSubtests = false;
  let firstRunWithoutSubtests = true;

  for (const groupedStep of groupedSteps) {
    const isSubTest = !!groupedStep.at(0)?.testStep;

    if (isSubTest) {
      hasSubtests = true;
      const nestedDependencies = await getNestedDependencies({
        cachingEnabled: false,
        testId: groupedStep.at(0)!.testStep!.id,
        tempDependencies: [],
        platform: config.platform,
        device: config.device,
        language: config.language,
      });

      runs.push(...nestedDependencies);
    } else {
      if (config.fromParentRun) {
        runs.push({
          cache: { enabled: false, data: [] },
          promptSteps: groupedStep,
          settings: config.test?.settings ?? {
            launchParams: [],
            preRequests: [],
            teardownRequests: [],
            inputKeys: [],
          },
          testId: config.test?.id,
          title: config.test?.title,
          isPartialExecution: config.isPartialExecution,
          platform: config.platform,
          device: config.device,
          locale: config.language,
          ...(config.parsedNestedDependencies != null && {
            dependencyInputValues:
              config.parsedNestedDependencies.length === 0
                ? []
                : config.test?.dependencyInputValues ?? [],
          }),
        });
      } else {
        let settings: TestSettings = {
          launchParams: config.test?.settings?.launchParams ?? [],
          preRequests: [],
          teardownRequests: config.test?.settings?.teardownRequests ?? [],
          inputKeys: config.test?.settings?.inputKeys ?? [],
        };

        if (firstRunWithoutSubtests) {
          settings = {
            ...settings,
            preRequests:
              config.test?.settings?.preRequests?.map((preRequest) => ({
                ...preRequest,
                delayBeforeRequest: preRequest.delayBeforeRequest ?? 0,
              })) ?? [],
          };
        }

        runs.push({
          testId: config.test?.id!,
          promptSteps: groupedStep,
          title: config.test?.title!,
          cache: { enabled: false, data: [] },
          platform: config.platform,
          device: config.device,
          settings,
          dependencyInputValues: config.test?.dependencyInputValues ?? [],
          // Carry this dependency's own authored source so the editor can build
          // a per-run markdown `globalTestTask` from it. Without this the editor
          // stamps the parent's markdown onto dependency runs, making the
          // dependency box execute the whole parent flow (and get saved as the
          // dependency baseline). `fromParentRun` runs (the parent itself) are
          // intentionally left without `commands` so the editor uses the live
          // editor doc, which may hold unsaved edits.
          commands: config.test?.commands,
        });

        firstRunWithoutSubtests = false;
      }
    }
  }

  return { runs, hasSubtests };
};

const handleCaching = async (
  groupedSteps: PromptStep[][],
  baseline: CachedScreenData[],
  config: BaseConfig & {
    organisationId: string;
    isPartialExecution?: boolean;
  }
) => {
  const { allowCachedRun, cachedData } = await prepareCachedFlow(
    groupedSteps.at(0)!,
    baseline,
    config.cachingEnabled,
    config.platform,
    config.organisationId,
    config.isPartialExecution
  );

  return { allowCachedRun, cachedData };
};

const getNestedDependencies = async ({
  testId,
  tempDependencies = [],
  cachingEnabled,
  platform,
  device,
  language,
}: BaseConfig & {
  tempDependencies: any[];
}): Promise<any[]> => {
  const testRef = doc(customFirestore, `custom-tests/${testId}`);
  const testSnap = await getDoc(testRef);
  const testData = { id: testSnap.id, ...testSnap.data() } as Test;

  const subCollectionRef = collection(
    customFirestore,
    `custom-tests/${testId}/dependencies`
  );
  const subCollectionSnapshot = await getDocs(subCollectionRef);

  const promptSteps = getGptDriverInput(testData.commands);
  const groupedSteps = groupSteps(promptSteps);

  const { runs: newTempDependencies, hasSubtests } = await processGroupedSteps(
    groupedSteps,
    {
      platform,
      device,
      testId,
      cachingEnabled,
      test: testData,
      language,
      fromParentRun: false,
    }
  );

  if (!hasSubtests) {
    const baseline = testData.cachedSteps?.[platform]?.[device] ?? [];
    const { allowCachedRun, cachedData } = await handleCaching(
      groupedSteps,
      baseline,
      {
        platform,
        device,
        testId,
        cachingEnabled,
        language,
        organisationId: testData.organisationId,
      }
    );

    newTempDependencies[0] = {
      ...newTempDependencies[0],
      cache: { enabled: allowCachedRun, data: cachedData },
    };
  }

  newTempDependencies.push(...tempDependencies);

  if (subCollectionSnapshot.empty) {
    return newTempDependencies;
  }

  const dependencyDoc = subCollectionSnapshot.docs[0];
  const currentTestDependencyIds = tempDependencies.map(
    (testDependency) => testDependency.testId
  );

  if (currentTestDependencyIds.includes(dependencyDoc.id)) {
    return tempDependencies;
  }

  return await getNestedDependencies({
    cachingEnabled,
    platform,
    device,
    testId: dependencyDoc.data().testId,
    tempDependencies: newTempDependencies,
    language,
  });
};

const getRuns = async ({
  parsedNestedDependencies,
  steps,
  companyData,
  sessionConfig,
  isPartialExecution,
  test,
  baseline,
  buildData,
}: {
  parsedNestedDependencies?: RunExecutionData[];
  steps: PromptStep[];
  companyData: CompanyData;
  sessionConfig: SessionConfigData;
  isPartialExecution: boolean;
  test?: Test;
  baseline: CachedScreenData[];
  buildData: BuildData;
}): Promise<RunExecutionData[]> => {
  const groupedSteps = groupSteps(steps);

  const { runs, hasSubtests } = await processGroupedSteps(groupedSteps, {
    platform: sessionConfig.platform,
    device: sessionConfig.device,
    testId: test?.id ?? "",
    cachingEnabled: companyData.settings.cachingEnabled ?? false,
    fromParentRun: true,
    test,
    isPartialExecution,
    parsedNestedDependencies,
    language: sessionConfig.language,
  });

  if (!hasSubtests) {
    const { allowCachedRun, cachedData } = await handleCaching(
      groupedSteps,
      baseline,
      {
        platform: buildData.platform,
        device: sessionConfig.device,
        testId: test?.id ?? "",
        cachingEnabled: companyData.settings.cachingEnabled ?? false,
        organisationId: buildData.organisationId,
        isPartialExecution,
        language: sessionConfig.language,
      }
    );

    const allRuns = [
      ...(parsedNestedDependencies ?? []),
      {
        ...(runs[0] as RunExecutionData),
        order: (parsedNestedDependencies?.length ?? 0) + 1,
        cache: { enabled: allowCachedRun, data: cachedData },
        os: sessionConfig.os,
      },
    ];

    // Merge launch params from all runs, parent takes priority on key conflicts
    const parentRun = allRuns[allRuns.length - 1];
    if (parentRun.settings) {
      parentRun.settings.launchParams = mergeLaunchParams(allRuns);
    }

    return allRuns;
  }

  const allRuns = [
    ...(parsedNestedDependencies ?? []),
    ...runs.map(
      (dependency, index) =>
        ({
          ...dependency,
          order: (parsedNestedDependencies?.length ?? 0) + index + 1,
          locale: sessionConfig.language,
          orientation: sessionConfig.orientation,
          os: sessionConfig.os,
        } as RunExecutionData)
    ),
  ];

  // Merge launch params from all runs, parent takes priority on key conflicts
  const parentRun = allRuns[allRuns.length - 1];
  if (parentRun.settings) {
    parentRun.settings.launchParams = mergeLaunchParams(allRuns);
  }

  return allRuns;
};

export { getNestedDependencies, getRuns };
